/*
 * Copyright (c) 2024-2026 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import {Component, HostListener, OnInit, signal} from '@angular/core';
import {EsciComponent} from '../esci.component';
import {HttpEventType, HttpResponse} from '@angular/common/http';
import {VehicleBuildCompare} from '../../model/vehicle-build-compare';
import {ColDef, ColGroupDef, GridApi, GridReadyEvent} from 'ag-grid-community';

@Component({
    selector: 'app-vehicle-build-compare',
    templateUrl: './vehicle-build-compare.component.html',
    styleUrls: ['./vehicle-build-compare.component.scss'],
    standalone: false
})
export class VehicleBuildCompareComponent extends EsciComponent implements OnInit {
  permissionUrl = '/esciUI/vehicleBuildComparePermission';
  vehicleBuildFile1: File = null;
  vehicleBuildFile2: File = null;
  progress = signal(0);
  showFileInput = true;
  maxFileSizeMessage = ' Maximum file size is ' + Math.round(this.MAX_FILE_SIZE / 1000) + 'kb.';
  vehicleBuildCompare: VehicleBuildCompare;
  rowData = [];
  readonly rowDataSignal = signal<any[]>([]);
  private setRowData(rows: any[]): void {
    this.rowData = rows || [];
    this.rowDataSignal.set([...(this.rowData || [])]);
  }

  defaultColDef: ColDef = {
    floatingFilter: true,
    resizable: true,
    sortable: true,
    filter: true,
    minWidth: 70
  };
  cols:  (ColDef | ColGroupDef)[] = [
    {
      headerName: 'Build ID',
      field: 'buildId',
    },
    {
      headerName: 'In File 1 Only',
      field: 'rposInFile1Only',
      tooltipValueGetter: (params) => params.data.rposInFile1Only.length,
      valueGetter: (params) => params.data.rposInFile1Only.join(', ')
    },
    {
      headerName: 'In File 2 Only',
      field: 'rposInFile2Only',
      tooltipValueGetter: (params) => params.data.rposInFile2Only.length,
      valueGetter: (params) => params.data.rposInFile2Only.join(', ')
    },
    {
      headerName: 'Difference Count',
      field: 'diffCount'
    },
    {
      headerName: 'In Both',
      children: [
        {
          headerName: 'In Both Count',
          field: 'inBothCount',
        },
        {
          headerName: 'RPOs In Both',
          field: 'rposInBoth',
          columnGroupShow: 'closed',
          valueGetter: (params) => params.data.rposInBoth.join(', ')
        }
      ]
    }
  ];
  private gridApi: GridApi;

  private setProgress(value: number): void {
    this.progress.set(value);
  }

  @HostListener('window:resize', ['$event'])
  onResize(event: any) {
    if (this.gridApi && !this.gridApi.isDestroyed()) {
      this.gridApi.sizeColumnsToFit();
    }
  }
  onGridReady(params: GridReadyEvent) {
    this.gridApi = params.api;
  }

  ngOnInit(): void {
    this.esciService.getPermissionAndFileSizeLimit(this);
  }

  runReport(): void {
    if (this.vehicleBuildFile1 && this.vehicleBuildFile2
        && this.permission() && this.areFileSizesValid()) {
      this.setProgress(10);
      this.uploadService.uploadFilesForVehicleBuildCompare(this.vehicleBuildFile1, this.vehicleBuildFile2).subscribe({
        next: (event: any) => {
          if (event.type === HttpEventType.UploadProgress) {
            this.setProgress(Math.round(100 * event.loaded / event.total));
          } else if (event instanceof HttpResponse) {
            this.setProgress(0);
            if (event.body) {
              this.vehicleBuildCompare = new VehicleBuildCompare(event.body);
              this.setRowData(this.vehicleBuildCompare.transformDataForGrid());
              this.showFileInput = false;
              this.messageService.processSuccess('Vehicle Build Comparison report generated successfully.');
            } else {
              this.messageService.processError('Either no vehicle builds found or files were invalid. Please try again.');
            }
          }
        },
        error: (err: any) => {
          this.setProgress(0);
          console.log(err);
          this.messageService.processError('Could not run the vehicle build comparison tool. Please check the files provided and try again later.');
        }
      });
    }
  }
  private areFileSizesValid(): boolean {
    let valid = true;
    if (this.vehicleBuildFile1 && this.vehicleBuildFile1.size > this.MAX_FILE_SIZE) {
      this.messageService.processError('Vehicle Build file 1 is too large. Please select a smaller file.' + this.maxFileSizeMessage);
      valid = false;
    }
    if (this.vehicleBuildFile2 && this.vehicleBuildFile2.size > this.MAX_FILE_SIZE) {
      this.messageService.processError('Vehicle Build file 2 is too large. Please select a smaller file.' + this.maxFileSizeMessage);
      valid = false;
    }
    return valid;
  }

  exportReport() {
    this.excelService.exportGridDataAsExcelFile(this.gridApi, 'Vehicle_Build_Comparison_Report');
  }
}
